import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getUserDetailsByIdAPI } from "../api/account";
import api from "../controllers/api";

// update api details of user apart from role/hub --> eg. Reporting Manager, Trusted device ?? Pending from BE
//signup user POST "/account/signup"
//get details of user GET "/account"  --> GET {{serverurl}}/account/all?page=1&limit=100 ==> list all accounts
//get hubs -> list of hubs available in system GET "/hub/?page=1&limit=100"
//update hubs --> account_id-hub_id mapping POST "/hub/mapping"
//get roles -> list of roles available in system GET "/rbac/roles?page=1&limit=100"
//update roles --> account_id-role_id mapping POST "/account/rolemapping"

//get details of user GET "/account"
export const fetchUserDetails = createAsyncThunk(
  "user/fetchUserDetails",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/account/all?page=1&limit=1000000&inactive=true");

      //
      const users = response?.results || [];
      let filteredUsers = []; // here response is filtered such that users with driver role are filtered out
      if (users.length > 0) {
        filteredUsers = users.filter((user) => {
          return user.Roles && !user.Roles.some((role) => role.role_name === "driver");
        });
      }

      return filteredUsers;
    } catch (error) {
      // return thunkAPI.rejectWithValue(error.message);
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchHubDetails = createAsyncThunk(
  "user/fetchHubDetails",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/hub/?page=1&limit=100");
      const hubs = response?.results.map((hub) => ({
        hub_id: hub.id,
        hub_name: hub.hub_name,
        hub_address: `${hub.address_1}, ${hub.address_2 || ""}, ${hub.city}, ${hub.state}, ${hub.country}`,
      }));
      //
      return hubs;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchRoles = createAsyncThunk("user/fetchRoles", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get("/rbac/roles?page=1&limit=100");
    //
    const roles = response?.results.map((role) => ({
      role_id: role.id,
      role_name: role.role_name,
    }));
    //
    return roles;
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || error.message);
  }
});

export const updateRoleMapping = createAsyncThunk(
  "user/updateRoleMapping",
  async ({ accountId, roleId }, { rejectWithValue }) => {
    try {
      const response = await api.post("/account/rolemapping", {
        accountId,
        roleId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateHubMapping = createAsyncThunk(
  "user/updateHubMapping",
  async ({ account_id, hub_id }, { rejectWithValue }) => {
    try {
      const response = await api.post("/hub/mapping", {
        account_id,
        hub_id,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Async thunk to fetch user details by ID
export const fetchUserDetailsWithId = createAsyncThunk(
  "getUserDetailsWithId/fetchUserDetailsWithId",
  async (userID, { rejectWithValue }) => {
    try {
      const response = await getUserDetailsByIdAPI(userID);
      // return response.result[0]; // we only need the first object
      return response; // we only need the first object
    } catch (error) {
      // return rejectWithValue(error?.response?.data || error.message);
      throw error;
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState: {
    users: [],
    status: "idle",
    error: null,
    hubs: [],
    hubsStatus: "idle",
    hubsError: null,
    roles: [],
    deletedAt: "",
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Handle fetchUserDetails

      .addCase(fetchUserDetails.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.users = action.payload;

        state.deletedAt = action.payload;
        //
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Handle fetchHubDetails
      .addCase(fetchHubDetails.pending, (state) => {
        state.hubsStatus = "loading";
      })
      .addCase(fetchHubDetails.fulfilled, (state, action) => {
        state.hubsStatus = "succeeded";
        state.hubs = action.payload;
      })
      .addCase(fetchHubDetails.rejected, (state, action) => {
        state.hubsStatus = "failed";
        state.hubsError = action.payload;
      })
      //Handle fetchRoles
      .addCase(fetchRoles.pending, (state) => {
        state.rolesStatus = "loading";
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.rolesStatus = "succeeded";
        state.roles = action.payload;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.rolesStatus = "failed";
        state.rolesError = action.payload;
      })
      //handle role mapping to user account
      .addCase(updateRoleMapping.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRoleMapping.fulfilled, (state, action) => {
        state.loading = false;
        // Handle successful role mapping update
      })
      .addCase(updateRoleMapping.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

const userDetailsWithIdSlice = createSlice({
  name: "getUserDetailsWithId",
  initialState: {
    data: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearUserDetails: (state) => {
      state.data = null;
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDetailsWithId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserDetailsWithId.fulfilled, (state, action) => {
        state.loading = false;
        // state.data = action.payload;
        state.data = action.payload.result[0];
      })
      .addCase(fetchUserDetailsWithId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default userSlice.reducer;
export const userDetailsWithId = userDetailsWithIdSlice.reducer;
